<template>
  <!-- The trial signup form, in the Buy IMPROVE page's card style since 5 Oct
       2026. Also embedded in the Cyber Security page, whose content still sits
       inside the old #signup-page stylesheet: the few rules that stylesheet
       sets on every heading, paragraph and link are matched below. -->
  <div class="trial-form">
    <h2 class="trial-form__title">Your details</h2>
    <v-form ref="form" @submit.prevent="submitForm">
      <v-row dense>
        <v-col cols="12" sm="6">
          <label class="trial-form__label">First name</label>
          <v-text-field
            v-model="first_name"
            :rules="[rules.required, rules.name]"
            :error-messages="errorMessages.first_name"
            placeholder="John"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="trial-form__label">Last name</label>
          <v-text-field
            v-model="last_name"
            :rules="[rules.required, rules.name]"
            :error-messages="errorMessages.last_name"
            placeholder="Smith"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12">
          <label class="trial-form__label">Company name</label>
          <v-text-field
            v-model="company_name"
            :rules="[rules.required]"
            :error-messages="errorMessages.company_name"
            placeholder="Smith Inc"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="trial-form__label">Work email</label>
          <v-text-field
            v-model="email"
            :rules="[rules.required, rules.email]"
            :error-messages="errorMessages.email"
            placeholder="johnsmith@email.com"
            type="email"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <!-- Required (Muhammad, 5 Oct 2026): the team calls every trial. -->
          <label class="trial-form__label">Phone</label>
          <v-text-field
            v-model="phone"
            :rules="[rules.required, rules.phone]"
            :error-messages="errorMessages.phone"
            placeholder="07123 456789"
            type="tel"
            autocomplete="tel"
            maxlength="64"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="trial-form__label">Password</label>
          <v-text-field
            v-model="password"
            :rules="[rules.required, rules.password]"
            :error-messages="errorMessages.password"
            placeholder="*******"
            type="password"
            maxlength="50"
            hint="8 or more characters, with an upper case letter, a lower case letter and a symbol"
            persistent-hint
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="trial-form__label">Confirm password</label>
          <v-text-field
            v-model="confirm_password"
            :rules="[rules.required]"
            :error-messages="errorMessages.confirm_password"
            placeholder="*******"
            type="password"
            outlined
          ></v-text-field>
        </v-col>
      </v-row>
      <v-checkbox
        v-model="newsletter"
        label="I would like to receive the latest news and updates from IMPROVE"
        class="trial-form__newsletter"
        hide-details
      ></v-checkbox>
      <p class="trial-form__terms">
        By signing up to this free trial you are agreeing to our
        <a :href="frontEndUrl + '/eula'">Terms and Conditions</a>. For details of
        how we use personal data please see our
        <a :href="frontEndUrl + '/privacy-policy'">Privacy Policy</a>. During your
        trial we will email you a few tips and reminders to help you get the most
        from IMPROVE. You can unsubscribe from these at any time.
      </p>
      <v-btn :loading="loading" type="submit" block depressed class="trial-form__submit">
        Start your free trial
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import AuthAPI from "@/api/auth.js";
import { IMPROVE_CONFIG } from "@/config.js";
import { track } from "@/utils/analytics";

export default {
  name: "RegisterForm",
  data() {
    return {
      frontEndUrl: IMPROVE_CONFIG.FRONTEND_URL,
      errorMessages: {},
      first_name: "",
      last_name: "",
      company_name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      newsletter: false,
      loading: false,
      rules: {
        required: value => !!value || "Required.",
        email: value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Invalid email.",
        // As on the Buy IMPROVE page: digits, spaces, +, brackets and dashes,
        // with 7 to 15 digits (UK numbers are 10 or 11, international with a
        // country code up to 15).
        phone: value => {
          const digits = String(value || "").replace(/\D/g, "");
          return (
            (/^[\d\s+()-]+$/.test(String(value || "").trim()) && digits.length >= 7 && digits.length <= 15) ||
            "Enter a valid phone number."
          );
        },
        name: value => (value && value.length >= 2) || "Name must be at least 2 characters.",
        // The API's own rule (RegisterUserRequest), so a weak password is
        // caught here and not after the form is sent: 8 to 50 characters
        // with a lower case letter, an upper case letter and one of its
        // symbols (only these count).
        password: value => {
          const v = String(value || "");
          return (
            (v.length >= 8 && v.length <= 50 && /[a-z]/.test(v) && /[A-Z]/.test(v) && /[?@#$%^&+=!*()-]/.test(v)) ||
            "Use 8 or more characters, with an upper case letter, a lower case letter and one of ? @ # $ % ^ & + = ! * ( ) -"
          );
        }
      }
    };
  },
  // No metaInfo: this app has no vue-meta, so it did nothing here, and a form
  // is not a page (the marketing site's copy dropped it after it overrode a
  // page's title there).
  //
  created() {
    // Nothing is kept on the visitor's device any more: the source marker is
    // read from the address when the form is sent (register). Clear what
    // older builds stored, without consent: the `afi` affiliate id (unused
    // since 2023; dropped with the website's, plan decision D3) and the
    // marker itself.
    try {
      localStorage.removeItem("refID");
      localStorage.removeItem("signupRef");
    } catch (e) {
      /* storage blocked: nothing to remove */
    }
  },
  methods: {
    submitForm() {
      if (this.password !== this.confirm_password) {
        this.$snotify.error("Password and Confirm Password do not match");
        return;
      }
      if (this.$refs.form.validate()) {
        this.register();
      }
    },
    register() {
      this.loading = true;
      // `ref=website` on the signup address is the marketing site's trial
      // button: RegisterUserAction sets companies.is_referred = 1 for it.
      const refer = String(this.$route.query.ref || "").trim();
      AuthAPI.register({
        refer: refer,
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        company_name: this.company_name,
        phone: this.phone.trim(),
        password: this.password,
        confirm_password: this.confirm_password,
        newsletter: this.newsletter,
        banding: 1
      })
        .then(() => {
          // No auto-login (Muhammad, 18 Sep 2026: "it should not be auto
          // login"; Sensolearn's form had logged the new admin straight in
          // since 2024). The account exists; the person is told so and signs
          // in with the email and password they just chose. The login page
          // then routes a trial admin to /welcome.
          this.loading = false;
          track("sign_up", { method: "app_signup" });
          this.$router.push({ path: "/thankyou", query: { email: this.email } });
        })
        .catch(err => {
          this.loading = false;
          this.errorMessages = this.$gf.errorResponse(err);
        });
    }
  }
};
</script>

<style scoped>
.trial-form {
  --navy: #002458;
  --teal: #08a0a8;
  --grey: #6b7280;
  --line: #e6ebf1;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 30px 30px 34px;
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.06);
  font-family: "Sora", sans-serif;
  text-align: left;
}

.trial-form__title,
#signup-page .trial-form__title {
  color: var(--navy);
  font-size: 21px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 18px;
  padding: 0;
}

.trial-form__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--navy);
  margin: 6px 0;
}

.trial-form >>> .v-text-field--outlined fieldset {
  border-color: var(--line);
}

.trial-form >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--teal);
}

.trial-form__newsletter {
  margin-top: 0;
  padding-top: 0;
}

.trial-form__newsletter >>> .v-label {
  font-size: 13px;
  color: #334155;
}

.trial-form__terms,
#signup-page .trial-form__terms {
  font-size: 13px;
  line-height: 1.6;
  color: var(--grey);
  margin: 18px 0 20px;
}

.trial-form__terms a,
#signup-page .trial-form__terms a {
  font-size: 13px;
  line-height: 1.6;
  color: var(--teal);
  font-weight: 600;
  text-decoration: none;
}

.trial-form__terms a:hover {
  text-decoration: underline;
}

.trial-form__submit.v-btn {
  background-color: var(--navy) !important;
  color: #fff !important;
  text-transform: none;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.01em;
  border-radius: 8px;
  height: 52px !important;
}

.trial-form__submit.v-btn:hover {
  background-color: var(--teal) !important;
}

@media (max-width: 600px) {
  .trial-form {
    padding: 22px 18px 26px;
  }
}
</style>
